import { useEffect, useRef, useState } from 'react';

import { type H2ODriveConfig } from '../../../drive/types';
import { useS3Service } from '../../../drive/useS3Service';

interface UseAutoConnectionProps {
  activeWorkspaceInitialized: boolean;
  stsDriveUrl?: string;
  workspaceDriveUrl?: string;
  activeWorkspaceName?: string;
}

export const useAutoConnection = (props: UseAutoConnectionProps) => {
  const { activeWorkspaceInitialized, stsDriveUrl, workspaceDriveUrl, activeWorkspaceName } = props;
  const s3Service = useS3Service();
  const [isAutoConnecting, setIsAutoConnecting] = useState(false);
  const [autoConnectionError, setAutoConnectionError] = useState<string | null>(null);
  const lastConnectedWorkspace = useRef<string | null>(null);
  const lastAttemptedWorkspace = useRef<string | null>(null);

  useEffect(() => {
    const autoConnect = async () => {
      if (!activeWorkspaceInitialized || !stsDriveUrl || !workspaceDriveUrl || !activeWorkspaceName) return;

      const workspaceChanged = lastConnectedWorkspace.current !== activeWorkspaceName;
      const shouldReconnect = !s3Service.isConnected || workspaceChanged;

      // Prevent retry if we already failed to connect to this workspace
      if (autoConnectionError && lastAttemptedWorkspace.current === activeWorkspaceName) {
        return;
      }

      if (!shouldReconnect || isAutoConnecting) return;

      try {
        setIsAutoConnecting(true);
        setAutoConnectionError(null);
        lastAttemptedWorkspace.current = activeWorkspaceName;

        if (s3Service.isConnected && workspaceChanged) {
          s3Service.disconnect();
        }

        const config: H2ODriveConfig = {
          driveStsUrl: stsDriveUrl,
          driveS3Url: workspaceDriveUrl,
          workspaceId: activeWorkspaceName,
          roleArn: 'drivedrivedrivedrive',
        };

        const success = await s3Service.connect(config);
        if (!success) throw new Error('Connection failed');

        lastConnectedWorkspace.current = activeWorkspaceName;
      } catch (error) {
        console.error('Automatic H2O Drive connection failed:', error);
        setAutoConnectionError(error instanceof Error ? error.message : 'Unknown connection error');
      } finally {
        setIsAutoConnecting(false);
      }
    };

    if (activeWorkspaceInitialized && stsDriveUrl && workspaceDriveUrl && activeWorkspaceName) {
      void autoConnect();
    }
  }, [
    activeWorkspaceInitialized,
    stsDriveUrl,
    workspaceDriveUrl,
    activeWorkspaceName,
    s3Service.isConnected,
    isAutoConnecting,
  ]);

  return {
    s3Service,
    isAutoConnecting,
    autoConnectionError,
  };
};
